// "New location" as a page rather than a dialog. Nothing is persisted until
// Save, so the server's create contract only decides what has to be valid when
// Save is pressed — the page itself demands nothing beyond a name.
//
// This is deliberately a subset of the location detail page. Screens cannot be
// attached to a location that does not exist yet; the service card has no stream
// id to show; and the service service account binds this location to an identity
// provisioned FOR it, which cannot exist before it does. All three appear only
// after Save. What is left is the two things that are genuinely properties of
// the location itself, one of which is optional.

import { Button, Card, Input, toast } from "@pej/kore/components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { ApiError } from "@/api/error";
import { useCreateLocation } from "@/api/locations";
import type { CreateLocation } from "@/api/types";
import { toastMutationError } from "@/components/common/toast-mutation-error";
import {
    POSTING_PATH_DESCRIPTION,
    POSTING_PATH_LABEL,
    POSTING_PATH_PLACEHOLDER,
} from "@/features/locations/posting-path";
import { applyProblemDetails, Field, Form, useKoreForm } from "@/lib/forms";

export const Route = createFileRoute("/w/$workspaceId/locations/new")({
    component: NewLocationPage,
});

const newLocationSchema = z.object({
    name: z.string().trim().min(1, "Name is required"),
    // Optional on purpose. `streams.file_path` is NOT NULL but an empty string
    // satisfies it, the create endpoint does not validate it, and it is
    // patchable afterwards — so a location can be registered before anyone has
    // walked up to the POS terminal to read the path off it.
    filePath: z.string().trim().optional(),
});

type NewLocationValues = z.infer<typeof newLocationSchema>;

function NewLocationPage() {
    const { workspaceId } = Route.useParams();
    const navigate = useNavigate();
    const createLocation = useCreateLocation(workspaceId);
    const form = useKoreForm({
        schema: newLocationSchema,
        defaultValues: { name: "", filePath: "" },
    });

    async function onSubmit(values: NewLocationValues) {
        const body: CreateLocation = {
            name: values.name,
            // The wire wants a string; empty means "not configured yet".
            filePath: values.filePath ?? "",
        };
        try {
            const created = await createLocation.mutateAsync(body);
            toast.success("Location created");
            // Straight to the real page, where the stream id, the screens and
            // the rest of the settings live.
            navigate({
                to: "/w/$workspaceId/locations/$locationId",
                params: { workspaceId, locationId: created.location.id },
            });
        } catch (err) {
            if (err instanceof ApiError) {
                // Violations sit on their fields; anything else is a toast.
                const { onFields, unmapped } = applyProblemDetails(form, err);
                if (!onFields) toastMutationError("Create failed", err, { unmapped });
                return;
            }
            throw err;
        }
    }

    return (
        <div className="flex flex-col gap-6">
            <h1 className="text-xl font-semibold tracking-tight">New location</h1>
            <Card className="p-6">
                <h2 className="mb-4 text-base font-semibold">Location details</h2>
                <Form form={form} onSubmit={onSubmit} className="flex flex-col gap-4">
                    <Field
                        name="name"
                        control={form.control}
                        label="Name"
                        required
                        render={({ field }) => <Input {...field} placeholder="e.g. Downtown" />}
                    />
                    <Field
                        name="filePath"
                        control={form.control}
                        label={POSTING_PATH_LABEL}
                        optional
                        description={POSTING_PATH_DESCRIPTION}
                        render={({ field }) => <Input {...field} placeholder={POSTING_PATH_PLACEHOLDER} />}
                    />
                    <div className="flex justify-end gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate({ to: "/w/$workspaceId/locations", params: { workspaceId } })}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={createLocation.isPending}>
                            {createLocation.isPending ? "Creating…" : "Create location"}
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
}
